import { incrementRowId } from "./restoreDocxFormValues";

/**
 * Add row click listener for DOCX forms with grid layout
 * Handles the "+ Click to Add Row" button functionality
 */
export function addDocxRowClickListener(): () => void {
  const handleAddRowClick = (event: Event) => {
    const button = event.target as HTMLElement;
    
    // Verify this is a click-to-add button
    if (button.tagName !== 'BUTTON' || !button.textContent?.includes('Click to Add Row')) {
      return;
    }

    // Find the parent container (the div with data-table-id)
    const tableContainer = button.closest('div[data-table-id]');
    if (!tableContainer) {
      console.warn('Could not find table container');
      return;
    }

    // Get all children of the table container
    const allChildren = Array.from(tableContainer.children);
    const buttonIndex = allChildren.indexOf(button);
    
    if (buttonIndex < 1) {
      console.warn('Not enough divs above the button to copy');
      return;
    }

    // Get the last div immediately above the button
    const rowToCopy2 = allChildren[buttonIndex - 1] as HTMLElement;
    
    // Check if the row contains InlineText fields
    const hasInlineText = rowToCopy2.querySelector('[data-item-type="InlineText"]');

    // Clear input values in rows while keeping data-id attributes
    const clearInputValues = (row: HTMLElement) => {
      // Clear text inputs
      row.querySelectorAll('input[type="text"], input[type="date"]').forEach((input) => {
        (input as HTMLInputElement).value = '';
      });

      // Uncheck checkboxes
      row.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        (input as HTMLInputElement).checked = false;
      });

      // Uncheck radio buttons
      row.querySelectorAll('input[type="radio"]').forEach((input) => {
        (input as HTMLInputElement).checked = false;
      });

      // Reset select dropdowns to first option
      row.querySelectorAll('select').forEach((select) => {
        (select as HTMLSelectElement).selectedIndex = 0;
      });

      // Clear textareas
      row.querySelectorAll('textarea').forEach((textarea) => {
        (textarea as HTMLTextAreaElement).value = '';
      });
    };

    const updateRowId = (row: HTMLElement, setGuid: boolean = false) => {
      if(setGuid) {
        row.setAttribute('data-row-id', crypto.randomUUID());
        return;
      }
      const previousRowId = row.getAttribute("data-row-id") ?? crypto.randomUUID();
      row.setAttribute('data-row-id', incrementRowId(previousRowId));
    };

    if (hasInlineText) {
      // For InlineText, only clone the previous row (single row copy)
      const clonedRow = rowToCopy2.cloneNode(true) as HTMLElement;
      
      updateRowId(clonedRow);
      clearInputValues(clonedRow);

      // Insert cloned row just before the button
      button.parentElement?.insertBefore(clonedRow, button);

      console.log('Successfully added new row (InlineText) to grid layout');
    } else {
      // For non-InlineText fields, clone the previous row and the row above it (two row copy)
      if (buttonIndex < 2) {
        console.warn('Not enough divs above the button to copy for non-InlineText');
        return;
      }

      const rowToCopy1 = allChildren[buttonIndex - 2] as HTMLElement;

      // Clone the rows
      const clonedRow1 = rowToCopy1.cloneNode(true) as HTMLElement;
      const clonedRow2 = rowToCopy2.cloneNode(true) as HTMLElement;
      
      updateRowId(clonedRow1, true);
      updateRowId(clonedRow2);

      clearInputValues(clonedRow1);
      clearInputValues(clonedRow2);

      // Insert cloned rows just before the button
      button.parentElement?.insertBefore(clonedRow2, button);
      button.parentElement?.insertBefore(clonedRow1, clonedRow2);

      console.log('Successfully added new rows to grid layout');
    }
  };

  // Add event listener to the document (event delegation)
  document.addEventListener('click', handleAddRowClick);

  // Return cleanup function
  return () => {
    document.removeEventListener('click', handleAddRowClick);
  };
}
